import { JSX, useState } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { Filter } from "evaluation/EvaluationUtils";
import ScoreField from "evaluation/ScoreField";
import { asTitle, setElement } from "utils/Utils";
import DatePicker from "utils/elements/DatePicker";
import { UpdateFilterAction } from "./EvaluationView";

export type EvaluationFilterProps = {
	data: Filter;
	minScore: number;
	maxScore: number;
	labels: Array<string>;

	getScoreName: (score: number | null) => string;
	getScoreInfo: (score: number | null) => string;
	getScoreColour: (score: number | null) => string;

	onUpdate: (data: UpdateFilterAction) => void;
};

export default function EvaluationFilter(
	props: Readonly<EvaluationFilterProps>
): JSX.Element {
	const { t } = useTranslation();

	const [expanded, setExpanded] = useState(false);

	return (
		<Box
			sx={{
				marginTop: 2,
				marginBottom: 2,
				paddingRight: 1,
				border: 1,
				borderColor: "divider",
				borderRadius: 1,
				backgroundColor: "background.paper"
			}}>
			<Grid container alignItems={"center"}>
				<Grid item>
					<IconButton onClick={() => setExpanded(!expanded)}>
						{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
					</IconButton>
				</Grid>
				<Grid item flexGrow={1}>
					<Typography variant="subtitle1">
						{asTitle(t("filter"))}
					</Typography>
				</Grid>
			</Grid>
			<Collapse
				in={expanded}
				timeout="auto"
				sx={{
					paddingLeft: 2,
					paddingRight: 2,
					paddingBottom: expanded ? 2 : 0
				}}>
				<Grid
					container
					columnSpacing={{ xs: 0, lg: 3 }}
					rowSpacing={1}
					alignItems="flex-end">
					{props.labels.length > 0 && (
						<Grid
							item
							container
							lg={12}
							md={8}
							sm={10}
							xs={12}
							spacing={2}
							alignItems="center">
							<Grid item xs={4} lg={2}>
								<Typography variant="subtitle2">
									{asTitle(t("labels"))}
								</Typography>
							</Grid>
							<Grid item xs={8} lg={10}>
								{props.labels.map(label => (
									<FormControlLabel
										key={label}
										control={
											<Checkbox
												checked={(
													(props.data?.labels ??
														[]) as Array<string>
												).includes(label)}
												onChange={e =>
													props.onUpdate({
														field: "labels",
														value: setElement(
															e.target.checked,
															label,
															(props.data
																?.labels ??
																[]) as Array<string>
														)
													})
												}
											/>
										}
										label={label}
									/>
								))}
							</Grid>
						</Grid>
					)}

					<Grid
						item
						container
						lg={6}
						md={8}
						sm={10}
						xs={12}
						spacing={2}
						alignItems="flex-end">
						<Grid item xs={4}>
							<Typography variant="subtitle2">
								{asTitle(t("changes"))}
							</Typography>
						</Grid>
						<Grid item xs={4}>
							<Typography variant="caption">
								{t("from")}
							</Typography>
							<DatePicker
								disableFuture
								inputProps={{ variant: "standard" }}
								value={
									props.data?.changesFrom
										? (props.data.changesFrom as Date)
										: null
								}
								onChange={(date: Date | null): void => {
									if (date !== null) {
										date.setHours(0, 0, 0, 0);
									}
									props.onUpdate({
										field: "changesFrom",
										value: date
									});
								}}
							/>
						</Grid>
						<Grid item xs={4}>
							<Typography variant="caption">
								{t("until")}
							</Typography>
							<DatePicker
								disableFuture
								inputProps={{ variant: "standard" }}
								value={
									props.data?.changesUntil
										? (props.data.changesUntil as Date)
										: null
								}
								onChange={function (date: Date | null): void {
									if (date !== null) {
										date.setHours(23, 59, 59, 999);
									}
									props.onUpdate({
										field: "changesUntil",
										value: date
									});
								}}
							/>
						</Grid>
					</Grid>

					<Grid
						item
						container
						lg={6}
						md={8}
						sm={10}
						xs={12}
						spacing={2}
						alignItems="flex-end">
						<Grid item xs={4}>
							<Typography variant="subtitle2">
								{asTitle(t("offered"))}
							</Typography>
						</Grid>
						<Grid item xs={4}>
							<Typography variant="caption">
								{t("from")}
							</Typography>
							<DatePicker
								inputProps={{ variant: "standard" }}
								value={
									props.data?.offeredFrom
										? (props.data.offeredFrom as Date)
										: null
								}
								onChange={(date: Date | null): void => {
									if (date !== null) {
										date.setHours(0, 0, 0, 0);
									}
									props.onUpdate({
										field: "offeredFrom",
										value: date
									});
								}}
							/>
						</Grid>
						<Grid item xs={4}>
							<Typography variant="caption">
								{t("until")}
							</Typography>
							<DatePicker
								inputProps={{ variant: "standard" }}
								value={
									props.data?.offeredUntil
										? (props.data.offeredUntil as Date)
										: null
								}
								onChange={function (date: Date | null): void {
									if (date !== null) {
										date.setHours(23, 59, 59, 999);
									}
									props.onUpdate({
										field: "offeredUntil",
										value: date
									});
								}}
							/>
						</Grid>
					</Grid>

					<Grid
						item
						container
						lg={6}
						md={8}
						sm={11}
						xs={12}
						spacing={2}
						alignItems="center">
						<Grid item xs={4}>
							<Typography variant="subtitle2">
								{asTitle(t("location"))}
							</Typography>
						</Grid>
						<Grid item xs={8}>
							<FormGroup row>
								<FormControlLabel
									control={<Checkbox size="small" />}
									label={asTitle(t("school"))}
									value={props.data?.locationSchool ?? false}
									onChange={(_event, checked): void => {
										props.onUpdate({
											field: "locationSchool",
											value: checked
										});
									}}
								/>
								<FormControlLabel
									control={<Checkbox size="small" />}
									label={asTitle(t("werkplek"))}
									value={props.data?.locationWork ?? false}
									onChange={(_event, checked): void => {
										props.onUpdate({
											field: "locationWork",
											value: checked
										});
									}}
								/>
								<FormControlLabel
									control={<Checkbox size="small" />}
									label={asTitle(t("extern"))}
									value={
										props.data?.locationExternal ?? false
									}
									onChange={(_event, checked): void => {
										props.onUpdate({
											field: "locationExternal",
											value: checked
										});
									}}
								/>
							</FormGroup>
						</Grid>
					</Grid>

					<Grid
						item
						container
						lg={6}
						md={8}
						sm={10}
						xs={12}
						spacing={2}
						alignItems="center">
						<Grid item xs={4}>
							<Typography variant="subtitle2">
								{asTitle(t("achieved"))}
							</Typography>
						</Grid>
						<Grid item xs={8}>
							<FormGroup row>
								<FormControlLabel
									control={<Checkbox size="small" />}
									label={asTitle(t("yes"))}
									checked={
										props.data.achieved === true
									}
									onChange={(_event, checked): void => {
										props.onUpdate({
											field: "achieved",
											value: checked ? true : undefined
										});
									}}
								/>
								<FormControlLabel
									control={<Checkbox size="small" />}
									label={asTitle(t("no"))}
									checked={
										props.data.achieved === false
									}
									onChange={(_event, checked): void => {
										props.onUpdate({
											field: "achieved",
											value: checked ? false : undefined
										});
									}}
								/>
							</FormGroup>
						</Grid>
					</Grid>

					<Grid
						item
						container
						lg={6}
						md={8}
						sm={10}
						xs={12}
						spacing={2}
						alignItems="flex-start">
						<Grid item xs={4}>
							<Typography
								variant="subtitle2"
								sx={{ paddingTop: 3 }}>
								{asTitle(t("score"))}
							</Typography>
						</Grid>
						<Grid
							item
							container
							direction={props.maxScore - props.minScore > 5 ? "column" : "row"}
							xs={8}
							alignItems="flex-start"
							spacing={props.maxScore - props.minScore > 5 ? 0 : 2}>
							<Grid item xs={6}>
								<Grid container direction="column">
									<Grid item>
										<Typography variant="caption">
											{t("from")}
										</Typography>
									</Grid>
									<Grid item>
										<ScoreField
											value={
												(props.data
													.scoreMin as string) ?? ""
											}
											disabled={false}
											minScore={props.minScore}
											maxScore={props.maxScore}
											getScoreName={props.getScoreName}
											getScoreInfo={props.getScoreInfo}
											getScoreColour={
												props.getScoreColour
											}
											onChange={value => {
												props.onUpdate({
													field: "scoreMin",
													value: value
												});
											}}
										/>
									</Grid>
								</Grid>
							</Grid>
							<Grid item xs={6}>
								<Grid container direction="column">
									<Grid item>
										<Typography variant="caption">
											{t("until")}
										</Typography>
									</Grid>
									<Grid item>
										<ScoreField
											value={
												(props.data
													.scoreMax as string) ?? ""
											}
											disabled={false}
											minScore={props.minScore}
											maxScore={props.maxScore}
											getScoreName={props.getScoreName}
											getScoreInfo={props.getScoreInfo}
											getScoreColour={
												props.getScoreColour
											}
											onChange={value => {
												props.onUpdate({
													field: "scoreMax",
													value: value
												});
											}}
										/>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>

					<Grid
						item
						container
						lg={6}
						md={8}
						sm={10}
						xs={12}
						spacing={2}
						alignSelf={props.maxScore - props.minScore > 5 ? "flex-start" : "center"}
						alignItems="center">
						<Grid item xs={4}>
							<Typography variant="subtitle2">
								{asTitle(t("rated"))}
							</Typography>
						</Grid>
						<Grid item xs={8}>
							<FormGroup row>
								<FormControlLabel
									control={<Checkbox size="small" />}
									label={asTitle(t("yes"))}
									checked={
										props.data.evaluated === true
									}
									onChange={(_event, checked): void => {
										props.onUpdate({
											field: "evaluated",
											value: checked ? true : undefined
										});
									}}
								/>
								<FormControlLabel
									control={<Checkbox size="small" />}
									label={asTitle(t("no"))}
									checked={
										props.data.evaluated === false
									}
									onChange={(_event, checked): void => {
										props.onUpdate({
											field: "evaluated",
											value: checked ? false : undefined
										});
									}}
								/>
							</FormGroup>
						</Grid>
					</Grid>
				</Grid>
			</Collapse>
		</Box>
	);
}
